// ============================================
// FAQ 用户端页面（/faq）— 公开浏览 FAQ
// 侧边栏筛选 + 主内容区卡片/列表
// ============================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, ChevronRight, ChevronUp, ChevronDown, Eye, MessageCircleQuestion,
  BookOpen, Play, Plus, Edit3, MessageSquare,
  LayoutGrid, List as ListIcon, Tag, Layers, Lock, LifeBuoy,
} from 'lucide-react';
import { useFaqStore } from './faq-store';
import { useUserStore } from '../stores/user-store';
import FaqEditorModal from './FaqEditorModal';
import FaqExpandedView from './FaqExpandedView';
import FaqQuestionModal from './FaqQuestionModal';
import { useEditingLock } from './use-editing-lock';
import { faqAnswerToPlainText } from './faq-rich-content';
import { faqTagIds, faqTagLabels } from './faq-tag-utils';
import { useFaqSpace } from './FaqSpaceContext';
import type { FaqItem } from './types';

/** 标签对应渐变色（无封面时使用） */
const TAG_GRADIENTS: Record<string, string> = {
  '产品': 'from-sky-400 to-blue-500',
  '售后': 'from-emerald-400 to-teal-500',
  '技术支持': 'from-violet-400 to-purple-500',
  '支付': 'from-amber-400 to-orange-500',
  '测试': 'from-pink-400 to-rose-500',
};

export default function FaqPublicPage() {
  const {
    loading, items,
    fetchItems, fetchTags, fetchTagOrders, reorderItemsInTag,
    getFilteredItems, getPrimaryTags, getSecondaryTags,
    searchQuery, primaryTagFilter, secondaryTagFilter,
    setSearchQuery, setPrimaryTagFilter, setSecondaryTagFilter,
  } = useFaqStore();

  const { profile } = useUserStore();
  const isAdmin = !!profile?.is_faq_admin;
  const navigate = useNavigate();
  const { path } = useFaqSpace();

  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<FaqItem | null>(null);
  const [lockWarning, setLockWarning] = useState<string | null>(null);
  const [askOpen, setAskOpen] = useState(false);
  const [sortError, setSortError] = useState<string | null>(null);

  const { checkLock, acquireLock, releaseLock, lockExpiring, refreshActivity } = useEditingLock();

  useEffect(() => {
    fetchItems();
    fetchTags();
    fetchTagOrders();
  }, [fetchItems, fetchTags, fetchTagOrders]);

  const filteredItems = getFilteredItems();
  const displayItems = isAdmin ? filteredItems : filteredItems.filter(i => i.is_published);
  const activeSortTagId = secondaryTagFilter ?? primaryTagFilter;
  // 全量列表（用于标签计数，不受标签筛选影响）
  const allItems = isAdmin ? items : items.filter(i => i.is_published);
  const primaryTags = getPrimaryTags();
  const secondaryTags = getSecondaryTags();
  const allTags = [...primaryTags, ...secondaryTags];
  const primaryFilterLabel = primaryTags.find((tag) => tag.id === primaryTagFilter)?.name;
  const secondaryFilterLabel = secondaryTags.find((tag) => tag.id === secondaryTagFilter)?.name;

  function toggleExpand(id: string) {
    const isExpanding = !expandedIds.has(id);
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    // 展开时自动滚动到该问题顶部
    if (isExpanding) {
      requestAnimationFrame(() => {
        document.getElementById(`faq-item-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }
  function expandAll() { setExpandedIds(new Set(displayItems.map((i) => i.id))); }
  function collapseAll() { setExpandedIds(new Set()); }

  function handleCreate() { setEditingItem(null); setEditorOpen(true); }
  function handleEdit(item: FaqItem) {
    const lockInfo = checkLock(item.id);
    if (lockInfo.locked) {
      setLockWarning(`${lockInfo.lockedBy} 正在编辑此文章，请稍后再试`);
      return;
    }
    if (!acquireLock(item.id)) {
      setLockWarning('无法获取编辑锁，请稍后再试');
      return;
    }
    setEditingItem(item);
    setEditorOpen(true);
  }
  function handleEditorClose() {
    releaseLock();
    setEditorOpen(false);
    setEditingItem(null);
  }

  async function handleMoveInActiveTag(itemId: string, direction: 'up' | 'down') {
    if (!activeSortTagId) return;
    const currentIndex = displayItems.findIndex((item) => item.id === itemId);
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (currentIndex < 0 || targetIndex < 0 || targetIndex >= displayItems.length) return;

    const nextIds = displayItems.map((item) => item.id);
    [nextIds[currentIndex], nextIds[targetIndex]] = [nextIds[targetIndex], nextIds[currentIndex]];

    setSortError(null);
    try {
      await reorderItemsInTag(activeSortTagId, nextIds);
    } catch (error) {
      console.error('reorderItemsInTag failed:', error);
      setSortError('排序保存失败，请稍后重试');
    }
  }

  function extractText(content: string | null, maxLen = 200, format?: string): string {
    const full = faqAnswerToPlainText(content, format);
    return full.length > maxLen ? full.slice(0, maxLen) + '...' : full;
  }

  return (
    <div className="flex h-[calc(100vh-56px)] flex-col lg:flex-row">

      {/* ── 移动端顶部筛选区（仅 < lg 显示） ── */}
      <div className="lg:hidden shrink-0 border-b border-slate-200 bg-white">
        {/* 搜索 */}
        <div className="px-3 pt-3 pb-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="搜索问题..."
              className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-300 transition-all"
            />
          </div>
        </div>
        {/* 一级标签横滚 */}
        <div className="px-3 pb-2 flex items-center gap-1.5 overflow-x-auto scrollbar-hide">
          <button
            onClick={() => { setPrimaryTagFilter(null); setSecondaryTagFilter(null); }}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              !primaryTagFilter ? 'bg-sky-500 text-white' : 'bg-slate-100 text-slate-500'
            }`}
          >
            全部 {allItems.length}
          </button>
          {primaryTags.map((t) => {
            const count = allItems.filter(i => faqTagIds(i, allTags, 1).includes(t.id)).length;
            return (
              <button
                key={t.id}
                onClick={() => setPrimaryTagFilter(primaryTagFilter === t.id ? null : t.id)}
                className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  primaryTagFilter === t.id ? 'bg-sky-500 text-white' : 'bg-slate-100 text-slate-500'
                }`}
              >
                {t.name} {count}
              </button>
            );
          })}
        </div>
        {/* 二级标签横滚 */}
        {secondaryTags.length > 0 && (
          <div className="px-3 pb-2 flex items-center gap-1.5 overflow-x-auto scrollbar-hide border-t border-slate-100 pt-2">
            {secondaryTags.map((t) => (
              <button
                key={t.id}
                onClick={() => setSecondaryTagFilter(secondaryTagFilter === t.id ? null : t.id)}
                className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-medium transition-all ${
                  secondaryTagFilter === t.id ? 'bg-purple-500 text-white' : 'bg-purple-50 text-purple-500'
                }`}
              >
                {t.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── 左侧边栏：搜索 + 筛选（仅 ≥ lg 显示） ── */}
      <aside className="hidden lg:flex w-[220px] shrink-0 border-r border-slate-200 bg-white flex-col overflow-y-auto">
        {/* 搜索 */}
        <div className="p-4 pb-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="搜索问题..."
              className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-300 transition-all"
            />
          </div>
        </div>

        {/* 一级标签 */}
        <div className="px-4 pb-2">
          <p className="flex items-center gap-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
            <Tag size={11} />
            分类
          </p>
          <div className="space-y-0.5">
            <button
              onClick={() => { setPrimaryTagFilter(null); setSecondaryTagFilter(null); }}
              className={`w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                !primaryTagFilter ? 'bg-sky-50 text-sky-600' : 'text-slate-500 hover:bg-slate-50'
              }`}
            >
              全部
              <span className="float-right text-[10px] text-slate-400">{allItems.length}</span>
            </button>
            {primaryTags.map((t) => {
              const count = allItems.filter(i => faqTagIds(i, allTags, 1).includes(t.id)).length;
              return (
                <button
                  key={t.id}
                  onClick={() => setPrimaryTagFilter(primaryTagFilter === t.id ? null : t.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                    primaryTagFilter === t.id ? 'bg-sky-50 text-sky-600' : 'text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  {t.name}
                  <span className="float-right text-[10px] text-slate-400">{count}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* 二级标签 */}
        {secondaryTags.length > 0 && (
          <div className="px-4 pb-3">
            <div className="w-full h-px bg-slate-100 mb-3" />
            <p className="flex items-center gap-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
              <Layers size={11} />
              子分类
            </p>
            <div className="space-y-0.5">
              {secondaryTags.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setSecondaryTagFilter(secondaryTagFilter === t.id ? null : t.id)}
                  className={`w-full text-left px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all ${
                    secondaryTagFilter === t.id ? 'bg-purple-50 text-purple-600' : 'text-slate-400 hover:bg-slate-50'
                  }`}
                >
                  {t.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-auto p-4 pt-3 border-t border-slate-100 space-y-2">
          <button
            onClick={() => navigate(path('questions'))}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-slate-600 text-xs font-semibold rounded-xl border border-slate-200 hover:border-sky-200 hover:text-sky-600 hover:bg-sky-50 transition-all"
          >
            <MessageSquare size={14} />
            留言中心
          </button>
          <button
            onClick={() => setAskOpen(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-sky-50 text-sky-600 text-xs font-semibold rounded-xl hover:bg-sky-100 transition-all"
          >
            <MessageCircleQuestion size={14} />
            我要提问
          </button>

          {isAdmin && (
            <button
              onClick={handleCreate}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-sky-500 text-white text-xs font-semibold rounded-xl hover:bg-sky-600 shadow-sm transition-all"
            >
              <Plus size={14} />
              发布新 FAQ
            </button>
          )}
        </div>
      </aside>

      {/* ── 主内容区 ── */}
      <main className="flex-1 overflow-y-auto bg-slate-50/50 min-w-0">
        {/* 顶栏：计数 + 视图切换 */}
        <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-sm border-b border-slate-100 px-3 sm:px-6 py-3 flex items-center justify-between">
          <div className="text-xs text-slate-400">
            {primaryTagFilter && <span className="text-sky-500 font-medium">{primaryFilterLabel ?? '未命名标签'}</span>}
            {secondaryTagFilter && <span className="text-purple-500 font-medium"> / {secondaryFilterLabel ?? '未命名标签'}</span>}
            {!primaryTagFilter && !secondaryTagFilter && <span>全部</span>}
            <span className="ml-2">· 共 {displayItems.length} 条</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => navigate(path())}
              className="mr-1 hidden items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-sky-200 hover:bg-sky-50 hover:text-sky-600 sm:inline-flex"
            >
              <LifeBuoy size={14} />
              支持首页
            </button>
            <button
              onClick={() => navigate(path('questions'))}
              className="mr-1 hidden items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-sky-200 hover:bg-sky-50 hover:text-sky-600 sm:inline-flex"
            >
              <MessageSquare size={14} />
              留言中心
            </button>
            <button
              onClick={() => setAskOpen(true)}
              className="mr-1 hidden items-center gap-1.5 rounded-lg bg-sky-50 px-3 py-1.5 text-xs font-semibold text-sky-600 transition hover:bg-sky-100 sm:inline-flex"
            >
              <MessageCircleQuestion size={14} />
              我要提问
            </button>
            {viewMode === 'list' && (
              <>
                <button onClick={expandAll} className="hidden sm:block px-2 py-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors">展开全部</button>
                <span className="hidden sm:block text-slate-300">|</span>
                <button onClick={collapseAll} className="hidden sm:block px-2 py-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors">折叠全部</button>
                <div className="hidden sm:block w-px h-4 bg-slate-200 mx-1" />
              </>
            )}
            <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-lg transition-all ${viewMode === 'list' ? 'bg-sky-50 text-sky-600' : 'text-slate-400 hover:text-slate-600'}`}>
              <ListIcon size={15} />
            </button>
            <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-sky-50 text-sky-600' : 'text-slate-400 hover:text-slate-600'}`}>
              <LayoutGrid size={15} />
            </button>
          </div>
        </div>

        <div className="p-3 sm:p-6">
          {sortError && (
            <div className="mb-3 rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-600">
              {sortError}
            </div>
          )}

          {/* 加载 */}
          {loading && (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-sky-200 border-t-sky-500 rounded-full animate-spin" />
            </div>
          )}

          {/* 空状态 */}
          {!loading && displayItems.length === 0 && (
            <div className="text-center py-20">
              <BookOpen size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-400 text-sm">
                {searchQuery || primaryTagFilter ? '没有找到匹配的问题' : '暂无 FAQ 内容'}
              </p>
            </div>
          )}

          {/* ── Grid 卡片模式 ── */}
          {!loading && displayItems.length > 0 && viewMode === 'grid' && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
              {displayItems.map((item) => {
                const hasImage = !!item.cover_image;

                return hasImage ? (
                  /* ── 有封面图：大图卡片 ── */
                  <div
                    key={item.id}
                    className="group bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer flex flex-col"
                    onClick={() => navigate(path(item.id))}
                  >
                    <div className="relative aspect-[16/9] overflow-hidden">
                      <img src={item.cover_image ?? undefined} alt={item.question} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                      {isAdmin && !item.is_published && (
                        <div className="absolute top-3 left-3">
                          <span className="px-2 py-1 bg-amber-500/80 backdrop-blur-sm text-white text-[11px] font-medium rounded-lg">草稿</span>
                        </div>
                      )}
                      {isAdmin && (
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                          <span
                            onClick={(e) => { e.stopPropagation(); handleEdit(item); }}
                            className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1.5 px-4 py-2 bg-white rounded-xl text-sm font-medium text-slate-700 shadow-sm hover:bg-sky-50"
                          >
                            <Edit3 size={14} /> 编辑
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 flex flex-col p-4">
                      <h3 className="text-sm font-semibold text-slate-700 mb-1.5 line-clamp-2 group-hover:text-sky-600 transition-colors duration-300">
                        {item.question}
                      </h3>
                      {item.answer && (
                        <p className="text-xs text-slate-400 line-clamp-2 mb-3 flex-1">{extractText(item.answer, 80, item.answer_format)}</p>
                      )}
                      <div className="flex items-center justify-between text-[11px] text-slate-400 mt-auto pt-2.5 border-t border-slate-100">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {faqTagLabels(item, allTags, 1).map((t) => (
                            <span key={t} className="px-1.5 py-0.5 bg-sky-50 text-sky-600 rounded font-medium">{t}</span>
                          ))}
                          {faqTagLabels(item, allTags, 2).map((t) => (
                            <span key={t} className="px-1.5 py-0.5 bg-purple-50 text-purple-500 rounded font-medium">{t}</span>
                          ))}
                        </div>
                        <span className="flex items-center gap-1 shrink-0"><Eye size={11} /> {item.view_count}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* ── 无封面图：Q/A 紧凑卡片 ── */
                  <div
                    key={item.id}
                    className="group relative bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer flex flex-col"
                    onClick={() => navigate(path(item.id))}
                  >
                    {/* Q 区域 */}
                    <div className="px-4 sm:px-5 pt-4 sm:pt-5 pb-3">
                      <div className="flex items-start gap-3">
                        <span className="w-7 h-7 rounded-lg bg-sky-500 text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">Q</span>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-bold text-slate-700 leading-snug group-hover:text-sky-600 transition-colors line-clamp-2">
                            {item.question}
                          </h3>
                          {isAdmin && !item.is_published && (
                            <span className="inline-block mt-1 px-2 py-0.5 bg-amber-100 text-amber-600 text-[10px] font-semibold rounded-md">草稿</span>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* A 区域 */}
                    {item.answer && (
                      <div className="px-4 sm:px-5 pb-4 flex items-start gap-3">
                        <span className="w-7 h-7 rounded-lg bg-emerald-500 text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">A</span>
                        <p className="text-xs text-slate-400 leading-relaxed line-clamp-3 flex-1">{extractText(item.answer, 120, item.answer_format)}</p>
                      </div>
                    )}
                    {/* 底栏 */}
                    <div className="mt-auto px-4 sm:px-5 py-3 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {faqTagLabels(item, allTags, 1).map((t) => (
                          <span key={t} className="px-1.5 py-0.5 bg-sky-50 text-sky-600 rounded font-medium">{t}</span>
                        ))}
                        {faqTagLabels(item, allTags, 2).map((t) => (
                          <span key={t} className="px-1.5 py-0.5 bg-purple-50 text-purple-500 rounded font-medium">{t}</span>
                        ))}
                      </div>
                      <span className="flex items-center gap-1 shrink-0"><Eye size={11} /> {item.view_count}</span>
                    </div>

                    {/* 管理员编辑悬浮 — 移动端始终显示编辑按钮 */}
                    {isAdmin && (
                      <>
                        {/* 桌面端 hover 遮罩 */}
                        <div className="hidden sm:flex absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all rounded-2xl items-center justify-center">
                          <span
                            onClick={(e) => { e.stopPropagation(); handleEdit(item); }}
                            className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1.5 px-4 py-2 bg-white rounded-xl text-sm font-medium text-slate-700 shadow-md hover:bg-sky-50"
                          >
                            <Edit3 size={14} /> 编辑
                          </span>
                        </div>
                        {/* 移动端固定编辑按钮 */}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleEdit(item); }}
                          className="sm:hidden absolute top-3 right-3 p-2 bg-white/90 text-slate-500 rounded-lg shadow-sm border border-slate-200"
                        >
                          <Edit3 size={14} />
                        </button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* ── List 列表模式 ── */}
          {!loading && displayItems.length > 0 && viewMode === 'list' && (
            <div className="space-y-2">
              {displayItems.map((item, index) => {
                const isExpanded = expandedIds.has(item.id);
                const sortPosition = activeSortTagId ? index + 1 : null;
                return (
                  <div key={item.id} id={`faq-item-${item.id}`} className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-sky-200 hover:shadow-sm transition-all scroll-mt-12">
                    <button onClick={() => toggleExpand(item.id)} className="w-full flex items-start gap-2 sm:gap-3 px-3 sm:px-5 py-3 sm:py-4 text-left">
                      <span className={`mt-0.5 text-sky-400 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}>
                        <ChevronRight size={16} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="w-4 h-4 rounded bg-sky-500 text-white text-[9px] font-bold flex items-center justify-center shrink-0">Q</span>
                          <h3 className="font-semibold text-slate-700 text-sm group-hover:text-sky-600 transition-colors break-words">{item.question}</h3>
                          {isAdmin && !item.is_published && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-600 text-[10px] font-semibold rounded-md">草稿</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 sm:gap-2 mt-1.5 flex-wrap">
                          {faqTagLabels(item, allTags, 1).map((t) => (
                            <span key={t} className="px-2 py-0.5 bg-sky-50 text-sky-600 text-[11px] font-medium rounded">{t}</span>
                          ))}
                          {faqTagLabels(item, allTags, 2).map((t) => (
                            <span key={t} className="px-2 py-0.5 bg-purple-50 text-purple-500 text-[11px] font-medium rounded">{t}</span>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {isAdmin && (
                          <span onClick={(e) => { e.stopPropagation(); handleEdit(item); }}
                            className="flex items-center gap-1 px-2.5 py-1 bg-slate-50 text-slate-400 hover:bg-sky-50 hover:text-sky-600 text-[11px] font-medium rounded-lg transition-all cursor-pointer sm:opacity-0 sm:group-hover:opacity-100">
                            <Edit3 size={11} /> 编辑
                          </span>
                        )}
                        {isAdmin && activeSortTagId && sortPosition !== null && (
                          <span
                            onClick={(e) => e.stopPropagation()}
                            className="flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-1.5 py-1 text-[11px] font-semibold text-emerald-700 shadow-sm shadow-emerald-100/70 transition hover:border-emerald-300 hover:bg-emerald-100/70"
                            title={`当前标签排序第 ${sortPosition} 位`}
                          >
                            <span className="min-w-6 text-center tabular-nums">#{sortPosition}</span>
                            <span
                              role="button"
                              tabIndex={0}
                              aria-disabled={index === 0}
                              title="上移"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (index > 0) handleMoveInActiveTag(item.id, 'up');
                              }}
                              className={`flex h-5 w-5 items-center justify-center rounded-md transition ${
                                index === 0
                                  ? 'pointer-events-none text-emerald-200'
                                  : 'cursor-pointer text-emerald-600 hover:bg-white hover:text-emerald-800'
                              }`}
                            >
                              <ChevronUp size={12} />
                            </span>
                            <span
                              role="button"
                              tabIndex={0}
                              aria-disabled={index === displayItems.length - 1}
                              title="下移"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (index < displayItems.length - 1) handleMoveInActiveTag(item.id, 'down');
                              }}
                              className={`flex h-5 w-5 items-center justify-center rounded-md transition ${
                                index === displayItems.length - 1
                                  ? 'pointer-events-none text-emerald-200'
                                  : 'cursor-pointer text-emerald-600 hover:bg-white hover:text-emerald-800'
                              }`}
                            >
                              <ChevronDown size={12} />
                            </span>
                          </span>
                        )}
                        <span className="flex items-center gap-1 text-[11px] text-slate-400"><Eye size={12} /> {item.view_count}</span>
                      </div>
                    </button>

                    {isExpanded && (
                      <FaqExpandedView item={item} isAdmin={isAdmin} onEdit={handleEdit} />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      <div className="lg:hidden shrink-0 border-t border-slate-200 bg-white px-3 py-2 flex items-center gap-2 safe-area-pb">
          <button
            onClick={() => setAskOpen(true)}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 bg-sky-50 text-sky-600 text-xs font-semibold rounded-xl"
          >
            <MessageCircleQuestion size={14} />
            我要提问
          </button>
      {isAdmin && (
          <button
            onClick={handleCreate}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 bg-sky-500 text-white text-xs font-semibold rounded-xl shadow-sm"
          >
            <Plus size={14} />
            发布新 FAQ
          </button>
      )}
        </div>

      {/* 编辑器弹窗 */}
      {isAdmin && (
        <FaqEditorModal
          isOpen={editorOpen}
          onClose={handleEditorClose}
          editingItem={editingItem}
          lockExpiring={lockExpiring}
          onRefreshActivity={refreshActivity}
        />
      )}

      <FaqQuestionModal isOpen={askOpen} onClose={() => setAskOpen(false)} />

      {/* 编辑锁冲突提示 */}
      {lockWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setLockWarning(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl p-6 max-w-sm mx-4 text-center">
            <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-3">
              <Lock size={22} className="text-amber-500" />
            </div>
            <h3 className="text-base font-bold text-slate-800 mb-1">文章编辑中</h3>
            <p className="text-sm text-slate-500 mb-4">{lockWarning}</p>
            <button
              onClick={() => setLockWarning(null)}
              className="px-6 py-2 bg-slate-100 text-slate-700 text-sm font-medium rounded-xl hover:bg-slate-200 transition-all"
            >知道了</button>
          </div>
        </div>
      )}
    </div>
  );
}
